import { FC, useEffect } from 'react';
import {
  Control,
  FieldArrayWithId,
  UseFieldArrayRemove,
  UseFormSetValue,
  useController,
} from 'react-hook-form';
import styled from 'styled-components';

import { RemoveButton } from '@/components/Button';
import { Label } from '@/components/Forms';
import { Option } from '@/components/Forms/Dropdown';
import FormField from '@/components/Forms/FormField';
import Text from '@/components/Text';

import { FormDataT } from './PostModal';

export const CheckboxHolder = styled.div`
  margin-top: -8px;
`;

export const StyledLabel = styled(Label)`
  position: relative;
  cursor: pointer;
  user-select: none;
  margin: 0;
  padding-left: 25px;
`;

export const Checkmark = styled.span`
  width: 16px;
  height: 16px;
  top: 0;
  bottom: 0;
  left: 0;
  margin: auto 0;
  position: absolute;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  border-radius: 1px;
  transition: 0.2s ease-out all;
  &::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.colors.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: none;
  }
`;

export const Input = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 1px;
  width: 1px;
  &:checked ~ ${Checkmark} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: ${({ theme }) => theme.colors.purple};
    &::after {
      display: block;
    }
  }
`;

const Variation = styled.div`
  padding: 0 32px;
  padding-bottom: 24px;
  margin-left: -32px;
  margin-right: -32px;
  &:nth-child(even) {
    background: ${({ theme }) => theme.colors.grayscaleSuperLight};
  }
`;

const VariationBody = styled.div`
  display: grid;
  grid-gap: 24px;
`;

const VariationHeading = styled.div`
  padding-top: 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NumberBadge = styled(Text)`
  display: flex;
  height: 24px;
  width: 48px;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 100px;
`;

interface Props {
  index: number;
  fields: FieldArrayWithId<FormDataT, 'variations'>[];
  remove: UseFieldArrayRemove;
  control: Control<FormDataT>;
  channels?: Option<number>[];
  setValue: UseFormSetValue<FormDataT>;
}

const VariationBox: FC<Props> = ({
  index,
  fields,
  remove,
  channels,
  control,
  setValue,
}) => {
  const { field } = useController<FormDataT, `variations.${number}.is_instant`>(
    {
      name: `variations.${index}.is_instant`,
      control,
    },
  );

  useEffect(() => {
    if (field.value) {
      setValue(`variations.${index}.post_date`, '');
    }
  }, [field.value, setValue, index]);

  return (
    <Variation>
      {fields.length > 1 && (
        <VariationHeading>
          <NumberBadge $type="smallBold" $color="white">
            Nr. {index + 1}
          </NumberBadge>
          {index > 0 && (
            <RemoveButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(index);
              }}
            >
              Remove
            </RemoveButton>
          )}
        </VariationHeading>
      )}
      <VariationBody>
        <FormField<FormDataT, number>
          name={`variations.${index}.channel_id`}
          control={control}
          label="Facebook Page"
          type="select"
          options={channels || []}
        />
        <FormField<FormDataT>
          label="Date & Time"
          control={control}
          name={`variations.${index}.post_date`}
          type="datepicker"
          disabled={field.value}
        />
        <CheckboxHolder>
          <StyledLabel>
            Instant post
            <Input
              type="checkbox"
              checked={field.value}
              onChange={() => {
                field.onChange(!field.value);
              }}
            />
            <Checkmark />
          </StyledLabel>
        </CheckboxHolder>
      </VariationBody>
    </Variation>
  );
};

export default VariationBox;
